import { useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Link, Stack, Typography } from '@mui/material';
import PageShell from '../components/layout/PageShell';

const bibtexCitation = `@article{Beker_2025,
  title={Brendo: an open resource for uniquely transcribed genes in brain endothelial cells},
  url={http://dx.doi.org/10.1101/2025.10.25.684508},
  DOI={10.1101/2025.10.25.684508},
  publisher={openRxiv},
  author={Beker, Ozgur and Khorsand, Fereshteh Ramezani and Adebali, Ogun and Mustafaoglu, Nur},
  year={2025},
  month=Oct
}`;

const glossary = [
  ['Four-list RRA score', 'Negative log10 of the Robust Rank Aggregation p-value across four Brain-overexpression ranked lists. Higher values indicate stronger prioritization in the four-list RRA ranking.'],
  ['Lists represented', 'Number of displayed RRA results containing the gene: DESeq2 aggregate, DESeq2 bootstrap validation, WRST aggregate, and WRST bootstrap validation.'],
  ['DESeq2 aggregate', 'The gene belongs to the significant RRA result created from DESeq2-ranked tissue comparisons (RRA p < 0.05).'],
  ['DESeq2 bootstrap validation', 'The gene belongs to the RRA result generated from 100 DESeq2 subsampling runs using 60% of Brain samples per run (RRA p < 0.05).'],
  ['WRST aggregate', 'The gene belongs to the significant RRA result created from Wilcoxon rank-sum test comparisons (RRA p < 0.05).'],
  ['Mean significance', 'Mean −log10 adjusted p-value across tissue comparisons where the gene met the selected significance criterion.'],
  ['Comparison support', 'The number of significant pairwise comparisons, reported as n/15 for all tissues or n/7 for the selected subset.'],
  ['Brain median count', 'Median untransformed MRN-normalized count across the 37 Brain samples in the curated analysis count matrix.'],
  ['Brain detection', 'Percentage of those 37 Brain samples with an MRN-normalized count greater than zero.'],
  ['Endothelial context fraction', 'Fraction of available non-Brain HPA endothelial tissues where Brain capillary expression is at least 1 nCPM and at least fourfold the largest eligible endothelial-cluster value. N/A marks genes without an exact HPA match or the expression values required for this metric.'],
  ['Protein evidence', 'Detected in fresh human BMEC proteomics dataset PXD018602 after MaxQuant Q < 0.01 and contaminant/decoy filtering. “Not detected” is limited to this dataset.'],
  ['Perfusion context score', 'A contextual score based on significant Brain-overexpression against heart, liver, kidney, lung, and skin, weighted by tissue perfusion and averaged across four analysis configurations. It is not a gene-perfusion measurement or an RRA input.'],
];

export default function HelpPage() {
  const [copyStatus, setCopyStatus] = useState('');

  const copyBibtex = async () => {
    try {
      await navigator.clipboard.writeText(bibtexCitation);
      setCopyStatus('BibTeX copied');
    } catch {
      setCopyStatus('Copy unavailable; select the citation text to copy it manually.');
    }
  };

  return (
    <PageShell
      eyebrow="Documentation"
      title="Help & support"
      intro="Use this guide to choose a result view, interpret each field, export exact values, and follow the analysis back to its source."
    >
      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography component="h2" variant="h5">Getting started</Typography>
              <Stack component="ol" spacing={1.5} sx={{ pl: 2.5, color: 'text.secondary' }}>
                <li>Open Transmembrane Focus to review the four-list RRA ranking.</li>
                <li>Use All genes to select method, expression direction, and comparison scope.</li>
                <li>Read header explanations by hovering or focusing the information icon.</li>
                <li>Open View expression before comparing genes with similar significance.</li>
                <li>Export all, filtered, or selected rows; CSV files retain exact raw values.</li>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography component="h2" variant="h5">Citation and source</Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                Registered BibTeX citation:
              </Typography>
              <Box
                component="pre"
                aria-label="BibTeX citation"
                tabIndex={0}
                sx={{
                  mt: 2,
                  mb: 0,
                  p: 2,
                  overflowX: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: '#f7fafb',
                  fontSize: '0.78rem',
                  lineHeight: 1.6,
                  whiteSpace: 'pre',
                }}
              >
                <code>{bibtexCitation}</code>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={copyBibtex} aria-label="Copy BibTeX citation">
                  Copy BibTeX
                </Button>
                {copyStatus && (
                  <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
                    {copyStatus}
                  </Typography>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  component={Link}
                  href="https://doi.org/10.1101/2025.10.25.684508"
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                >
                  Brendo preprint
                </Button>
                <Button
                  component={Link}
                  href="https://github.com/CompGenomeLab/brendo"
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                >
                  Source and issue tracker
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ mt: 3 }}>
                Support: <Link href="mailto:ozgurbeker@sabanciuniv.edu">ozgurbeker@sabanciuniv.edu</Link>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography component="h2" variant="h4" sx={{ mb: 3 }}>Data dictionary</Typography>
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.34fr) 1fr' }, borderTop: '1px solid', borderColor: 'divider' }}>
          {glossary.map(([term, definition]) => (
            <Box key={term} sx={{ display: 'contents' }}>
              <Typography component="dt" sx={{ fontWeight: 800, py: 2.25, pr: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                {term}
              </Typography>
              <Typography component="dd" color="text.secondary" sx={{ m: 0, py: 2.25, borderBottom: '1px solid', borderColor: 'divider', lineHeight: 1.7 }}>
                {definition}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </PageShell>
  );
}
